import React, { useState, useEffect, useCallback } from 'react';
import { Lock, Users, Trash2, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getProjectNotes,
  addProjectNote,
  deleteProjectNote,
} from '@/services/projects.service';
import type { ProjectNote, ProjectNoteVisibility } from '@/types';
import { Button, LoadingState, EmptyState } from '@/components/common';

interface ProjectNotesPanelProps {
  projectId: number;
  currentUserId?: number;
  // Notifică părintele când se schimbă numărul de note (pentru badge-ul din antet)
  onCountChange?: (count: number) => void;
  // 'full' = formular + listă; 'list' = doar afișarea notelor; 'add' = doar formularul (în modal)
  mode?: 'full' | 'list' | 'add';
  // La schimbarea acestei valori, lista se reîncarcă (după adăugarea dintr-un modal separat)
  refreshSignal?: number;
  // Apelat după ce o notă a fost adăugată (pentru a reîmprospăta lista din pagină + a închide modalul)
  onAdded?: () => void;
}

// Formatare dată/oră în format românesc scurt
function formatNoteDate(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hh}:${mm}`;
  } catch {
    return '';
  }
}

export function ProjectNotesPanel({ projectId, currentUserId, onCountChange, mode = 'full', refreshSignal, onAdded }: ProjectNotesPanelProps) {
  const [notes, setNotes] = useState<ProjectNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState<ProjectNoteVisibility>('personal');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadNotes = useCallback(async () => {
    try {
      const data = await getProjectNotes(projectId);
      setNotes(data);
      onCountChange?.(data.length);
    } catch (err) {
      console.error('Failed to load notes:', err);
      toast.error('Nu s-au putut încărca notele.');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, onCountChange]);

  useEffect(() => {
    setIsLoading(true);
    loadNotes();
  }, [loadNotes, refreshSignal]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) {
      toast.error('Introduceți textul notei.');
      return;
    }
    setIsSubmitting(true);
    try {
      await addProjectNote(projectId, trimmed, visibility);
      setBody('');
      toast.success(
        visibility === 'shared'
          ? 'Notă partajată cu echipa.'
          : 'Notă personală adăugată.'
      );
      await loadNotes();
      onAdded?.();
    } catch (err) {
      console.error('Failed to add note:', err);
      toast.error('Nu s-a putut adăuga nota. Încercați din nou.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (noteId: number) => {
    setDeletingId(noteId);
    try {
      await deleteProjectNote(noteId);
      toast.success('Nota a fost ștearsă.');
      await loadNotes();
    } catch (err) {
      console.error('Failed to delete note:', err);
      toast.error('Nu s-a putut șterge nota.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Formular adăugare notă (ascuns în modul „doar listă") */}
      {mode !== 'list' && (
      <form onSubmit={handleSubmit} className="space-y-3">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Scrieți o notă despre acest proiect..."
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm resize-y"
        />

        {/* Toggle vizibilitate: Personală / Partajată cu echipa */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="inline-flex rounded-lg border border-gray-300 p-0.5 bg-gray-50 self-start">
            <button
              type="button"
              onClick={() => setVisibility('personal')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                visibility === 'personal'
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Lock className="w-3.5 h-3.5" />
              Personală
            </button>
            <button
              type="button"
              onClick={() => setVisibility('shared')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                visibility === 'shared'
                  ? 'bg-white text-primary-700 shadow-sm border border-gray-200'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users className="w-3.5 h-3.5" />
              Partajată cu echipa
            </button>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="sm"
            isLoading={isSubmitting}
            leftIcon={<Send className="w-4 h-4" />}
          >
            Adaugă notă
          </Button>
        </div>
        <p className="text-xs text-gray-500">
          {visibility === 'personal'
            ? 'Nota personală este vizibilă doar pentru dumneavoastră.'
            : 'Nota partajată este vizibilă pentru toți membrii proiectului.'}
        </p>
      </form>
      )}

      {/* Listă note (ascunsă în modul „doar adăugare") */}
      {mode !== 'add' && (
      <div className={mode === 'list' ? '' : 'border-t border-gray-100 pt-4'}>
        {isLoading ? (
          <LoadingState message="Se încarcă notele..." />
        ) : notes.length === 0 ? (
          <EmptyState
            icon="file"
            title="Nu există note"
            description="Adăugați prima notă pentru acest proiect."
          />
        ) : (
          <ul className="space-y-3">
            {notes.map((note) => {
              const isOwn = currentUserId !== undefined && note.author_id === currentUserId;
              const isShared = note.visibility === 'shared';
              return (
                <li
                  key={note.id}
                  className={`rounded-lg border p-3 ${
                    isShared
                      ? 'bg-primary-50/40 border-primary-100'
                      : 'bg-amber-50/40 border-amber-100'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                            isShared
                              ? 'bg-primary-100 text-primary-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {isShared ? (
                            <>
                              <Users className="w-3 h-3" />
                              Partajată
                            </>
                          ) : (
                            <>
                              <Lock className="w-3 h-3" />
                              Personală
                            </>
                          )}
                        </span>
                        {note.author_name && (
                          <span className="text-xs text-gray-500">{note.author_name}</span>
                        )}
                        <span className="text-xs text-gray-400">
                          {formatNoteDate(note.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                        {note.body}
                      </p>
                    </div>

                    {/* Ștergere — doar propriile note */}
                    {isOwn && (
                      <button
                        type="button"
                        onClick={() => handleDelete(note.id)}
                        disabled={deletingId === note.id}
                        title="Șterge nota"
                        className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      )}
    </div>
  );
}
